import { Theme } from '@mui/joy';
import { ColDef } from 'ag-grid-community';
import { t } from 'i18next';

import { ICliJob } from 'cli/types';
import { AutocompleteFilter } from 'core/components/AgGridTable';
import { LinkCell, StatusCell } from 'core/elements';
import { AgGridFilterOption, LinkTarget, SortDirection } from 'core/enums';
import { VersionTypeHelper } from 'core/helpers';
import { formatDate } from 'core/utils';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { orderPartsPath } from 'orders/routes/routePaths';
import { PricingHelper } from 'pricing/helpers';
import { statusColorMap } from 'quotes/constants';
import {
  EmployeeCell,
  employeeCellValueFormatter,
  ExternalOrderStatus,
  getExternalOrderStatusLabel,
  QuoteIdCell,
  QuoteStateCell,
} from 'quotes/elements';
import {
  ExternalOrderStatusType,
  QuoteEntityType,
  QuoteListType,
  QuoteState,
  QuoteStatus,
  QuoteStatusLabel,
} from 'quotes/enums';
import { IBaseQuote, ICustomerQuote } from 'quotes/types';

import { CustomerQuoteColId } from './enums';
import { getOrderStatusTooltip, getQuoteStatuses } from './utils';

const formatVersionTypeValue = (value: boolean | number) =>
  VersionTypeHelper.getVersionTypeLabel(VersionTypeHelper.determineVersionType(value));

export const allColumnDefs = (
  theme: Theme,
  entityType = QuoteEntityType.QUOTE,
  {
    pendingCoreCliJob,
    quoteListType = QuoteListType.GLOBAL,
  }: { pendingCoreCliJob?: ICliJob; quoteListType?: QuoteListType } = {}
): Record<string, ColDef> => {
  const isOrder = QuoteEntityType.ORDER === entityType;
  const isGlobal = quoteListType === QuoteListType.GLOBAL;

  return {
    // Common columns
    versionTypeColumn: {
      colId: CustomerQuoteColId.ALTERNATE_VERSION_ENABLED,
      headerName: t('headerVersionType'),
      field: 'alternateVersionEnabled',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [1, 0],
        valueFormatter: ({ value }: { value: number }) => formatVersionTypeValue(value),
      },
      valueFormatter: ({ value }: { value: number }) => formatVersionTypeValue(value),
      minWidth: 90,
      width: 90,
      maxWidth: 90,
      cellClass: 'paddingSmall',
      cellStyle: { alignItems: 'center' },
      cellRenderer: ({ value }: { value: number }) =>
        VersionTypeHelper.getVersionTypeChip(theme, value),
    },
    statusColumn: {
      colId: CustomerQuoteColId.STATUS,
      field: 'status',
      headerName: t('headerStatus'),
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }: { value: QuoteStatus }) => QuoteStatusLabel[value],
        values: getQuoteStatuses(entityType),
        defaultToNothingSelected: true,
      },
      cellRenderer: StatusCell,
      cellRendererParams: {
        colorMap: statusColorMap,
        labelMap: Object.fromEntries(Object.entries(QuoteStatusLabel)),
        // Surface why an order failed right on the chip (otherwise only visible in the details).
        getTooltip: getOrderStatusTooltip,
      },
    },
    externalStatusColumn: {
      colId: CustomerQuoteColId.EXTERNAL_STATUS,
      headerName: t('headerExternalStatus'),
      field: 'externalStatus',
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }: { value: ExternalOrderStatusType }) =>
          getExternalOrderStatusLabel(value as ExternalOrderStatusType),
        values: Object.values(ExternalOrderStatusType),
        defaultToNothingSelected: true,
      },
      cellRenderer: ({ value }) => <ExternalOrderStatus value={value} displayAsChip={false} />,
    },
    orderIdColumn: {
      colId: CustomerQuoteColId.ID,
      headerName: t('headerOrderId'),
      field: 'id',
      filter: 'agNumberColumnFilter',
      width: 100,
      cellRenderer: LinkCell,
      cellRendererParams: {
        getPath: (data: ICustomerQuote) => orderPartsPath(data.id.toString()),
        target: isGlobal ? LinkTarget.SELF : LinkTarget.BLANK,
      },
    },
    quoteIdColumn: {
      colId: isOrder ? CustomerQuoteColId.PARENT_ID : CustomerQuoteColId.ID,
      headerName: t('headerQuoteId'),
      field: isOrder ? 'parentId' : 'id',
      filter: 'agNumberColumnFilter',
      width: 100,
      cellRenderer: QuoteIdCell,
      cellRendererParams: {
        columnId: isOrder ? 'parentId' : 'id',
        target: isGlobal ? LinkTarget.SELF : LinkTarget.BLANK,
      },
    },
    customerColumn: {
      colId: CustomerQuoteColId.CUSTOMER_CONTACT_GROUP_NAME,
      headerName: t('headerCustomer'),
      field: 'customerContactGroupName',
      filter: 'agTextColumnFilter',
    },
    contactColumn: {
      colId: CustomerQuoteColId.CUSTOMER_CONTACT_NAME,
      headerName: t('headerContact'),
      field: 'customerContactName',
      filter: AutocompleteFilter,
      filterParams: {
        autocompleteProps: {
          getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
          placeholder: t('placeholderSelectUser'),
          requestPayload: {
            filters: {
              userType: CorporateUserType.CUSTOMER,
            },
          },
        },
        getAutocompleteValue: (option: ICorporateUserAutocomplete) => option.userId,
      },
    },
    jobColumn: {
      colId: CustomerQuoteColId.JOB_NAME,
      headerName: t('headerJob'),
      field: 'jobName',
      filter: 'agTextColumnFilter',
    },
    projectColumn: {
      colId: CustomerQuoteColId.VENDOR_PROJECT_NAME,
      headerName: t('headerProject'),
      field: 'vendorProjectName',
      filter: 'agTextColumnFilter',
    },
    totalPriceColumn: {
      colId: CustomerQuoteColId.TOTAL_PRICE,
      headerName: isOrder ? t('headerOrderValue') : t('headerReleaseValue'),
      field: 'totalPrice',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      valueFormatter: ({ data }: { data: ICustomerQuote }) =>
        PricingHelper.formatPricing({ amount: data.totalPrice }),
      cellStyle: { textAlign: 'right' },
    },
    userColumn: {
      colId: isOrder
        ? CustomerQuoteColId.VENDOR_CORPORATE_USER_NAME
        : CustomerQuoteColId.OWNER_USER_NAME,
      headerName: t('headerUser'),
      field: isOrder ? 'vendorCorporateUserName' : 'ownerUserName',
      filter: 'agTextColumnFilter',
    },
    locationColumn: {
      colId: CustomerQuoteColId.LOCATION_NAME,
      headerName: t('headerLocation'),
      field: 'locationName',
      filter: 'agTextColumnFilter',
    },
    verticalMarketColumn: {
      colId: CustomerQuoteColId.VERTICAL_MARKET_NAME,
      headerName: t('headerVerticalMarket'),
      field: 'verticalMarketName',
      filter: 'agTextColumnFilter',
    },
    updatedAtColumn: {
      colId: CustomerQuoteColId.UPDATED_AT,
      headerName: t('headerUpdated'),
      field: 'updatedAt',
      valueFormatter: ({ value }: { value: string }) => formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      sort: SortDirection.DESC,
    },
    createdAtColumn: {
      colId: CustomerQuoteColId.CREATED_AT,
      headerName: t('headerCreated'),
      field: 'createdAt',
      valueFormatter: ({ value }: { value: string }) => formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
    },
    archivedColumn: {
      colId: CustomerQuoteColId.ARCHIVED,
      headerName: t(`header${entityType}State`),
      field: 'archived',
      cellRenderer: QuoteStateCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressSelectAll: true,
        values: [QuoteState.ACTIVE, QuoteState.ARCHIVED],
      },
      sortable: false,
    },
    isrColumn: {
      colId: 'isr',
      headerName: t('headerIsr'),
      field: 'isr',
      minWidth: 250,
      filter: 'agTextColumnFilter',
      valueFormatter: employeeCellValueFormatter,
      cellRenderer: EmployeeCell,
    },
    osrColumn: {
      colId: 'osr',
      headerName: t('headerOsr'),
      field: 'osr',
      minWidth: 250,
      filter: 'agTextColumnFilter',
      valueFormatter: employeeCellValueFormatter,
      cellRenderer: EmployeeCell,
    },

    // Quote specific columns
    billToAccountNumberColumn: {
      colId: CustomerQuoteColId.BILL_TO_ACCOUNT_NUMBER,
      headerName: t('headerBillToARNumber'),
      field: 'billToAccountNumber',
      filter: 'agTextColumnFilter',
    },
    groupColumn: {
      colId: CustomerQuoteColId.CUSTOMER_CONTACT_GROUPING,
      headerName: t('headerGroup'),
      field: 'customerContactGrouping',
      filter: 'agTextColumnFilter',
    },
    closeConfidenceColumn: {
      colId: CustomerQuoteColId.CLOSE_CONFIDENCE,
      headerName: t('headerCloseConfidence'),
      field: 'closeConfidence',
      valueFormatter: ({ value }: { value: number }) => (value ? `${value}%` : ''),
      filter: 'agNumberColumnFilter',
    },
    closeDateColumn: {
      colId: CustomerQuoteColId.ESTIMATED_CLOSE_DATE,
      headerName: t('headerCloseDate'),
      field: 'estimatedCloseDate',
      valueFormatter: ({ value }: { value: string }) => value && formatDate(value),
      filter: 'agDateColumnFilter',
    },
    validUntilColumn: {
      colId: CustomerQuoteColId.VALID_UNTIL,
      headerName: t('headerValidUntil'),
      field: 'validUntil',
      valueFormatter: ({ value }: { value: string }) => value && formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        includeBlanksInGreaterThan: true,
      },
    },

    // Order specific columns
    customerNumberColumn: {
      colId: CustomerQuoteColId.EXTERNAL_CUSTOMER_NUMBER,
      headerName: t('headerCustomerNumber'),
      field: 'externalCustomerNumber',
      filter: 'agTextColumnFilter',
    },
    purchaseOrderNumberColumn: {
      colId: CustomerQuoteColId.PURCHASE_ORDER_NUMBER,
      headerName: t('headerPurchaseOrderNumber'),
      field: 'purchaseOrderNumber',
      filter: 'agTextColumnFilter',
    },
    externalOrderNumberColumn: {
      colId: CustomerQuoteColId.EXTERNAL_ORDER_NUMBER,
      headerName: t('headerExternalOrderNumber'),
      field: 'externalOrderNumber',
      filter: 'agTextColumnFilter',
      cellRenderer: LinkCell,
      cellRendererParams: {
        isLoading: !!pendingCoreCliJob,
        getPath: (data: IBaseQuote) => data.externalOrderUrl,
        target: LinkTarget.BLANK,
      },
    },
    externalPendingOrderNumberColumn: {
      colId: CustomerQuoteColId.EXTERNAL_PENDING_ORDER_NUMBER,
      headerName: t('headerExternalPendingOrderNumber'),
      field: 'externalPendingOrderNumber',
      filter: 'agTextColumnFilter',
      cellRenderer: LinkCell,
      cellRendererParams: {
        isLoading: !!pendingCoreCliJob,
        getPath: (data: IBaseQuote) => data.externalPendingOrderUrl,
        target: LinkTarget.BLANK,
      },
    },
  };
};
